
import { Component, Vue, Prop } from 'vue-property-decorator';
import { CalendarConfig } from '../core/list-layout.types';
import { getNestedProperty } from '../core/list-layout.utils';
import moment from 'moment';

interface CalendarDay {
    date: Date;
    dayNumber: number;
    isCurrentMonth: boolean;
    isToday: boolean;
    isWeekend: boolean;
    events: any[];
}

interface WeekDay {
    date: Date;
    dayName: string;
    dayNumber: number;
    isToday: boolean;
    events: any[];
}

@Component
export default class ListLayoutCalendar extends Vue {
    @Prop({ required: true }) config!: CalendarConfig;
    @Prop({ type: Array, default: () => [] }) items!: any[];
    @Prop({ type: Boolean, default: false }) loading!: boolean;

    viewMode: 'month' | 'week' | 'day' = this.config.viewMode;
    currentDate: Date = new Date();

    get weekdays(): string[] {
        const weekStart = this.config.weekStartsOn || 1;
        const days = [
            this.$i18n.t('listLayout.calendar.days.sun') as string,
            this.$i18n.t('listLayout.calendar.days.mon') as string,
            this.$i18n.t('listLayout.calendar.days.tue') as string,
            this.$i18n.t('listLayout.calendar.days.wed') as string,
            this.$i18n.t('listLayout.calendar.days.thu') as string,
            this.$i18n.t('listLayout.calendar.days.fri') as string,
            this.$i18n.t('listLayout.calendar.days.sat') as string,
        ];

        return [...days.slice(weekStart), ...days.slice(0, weekStart)];
    }

    get currentPeriodTitle(): string {
        const date = moment(this.currentDate);
        switch (this.viewMode) {
            case 'month':
                return date.format('MMMM YYYY');
            case 'week': {
                const weekStart = this.getWeekStart(this.currentDate);
                const weekEnd = moment(weekStart).add(6, 'days');
                return `${weekStart.format('DD.MM')} - ${weekEnd.format('DD.MM.YYYY')}`;
            }
            case 'day':
                return date.format('DD. MMMM YYYY');
            default:
                return '';
        }
    }

    get currentDayTitle(): string {
        return moment(this.currentDate).format('dddd, DD. MMMM YYYY');
    }

    get monthDays(): CalendarDay[] {
        const days: CalendarDay[] = [];
        const date = moment(this.currentDate);
        const startOfMonth = date.clone().startOf('month');
        const endOfMonth = date.clone().endOf('month');
        const weekStart = this.config.weekStartsOn || 1;

        let current = startOfMonth.clone().day(weekStart);
        if (current.isAfter(startOfMonth)) {
            current.subtract(7, 'days');
        }

        const end = endOfMonth.clone().day(weekStart + 6);
        if (end.isBefore(endOfMonth)) {
            end.add(7, 'days');
        }

        while (current.isSameOrBefore(end)) {
            const dayDate = current.toDate();
            days.push({
                date: dayDate,
                dayNumber: current.date(),
                isCurrentMonth: current.month() === date.month(),
                isToday: current.isSame(moment(), 'day'),
                isWeekend: current.day() === 0 || current.day() === 6,
                events: this.getEventsForDay(dayDate),
            });
            current.add(1, 'day');
        }

        return days;
    }

    get weekDays(): WeekDay[] {
        const days: WeekDay[] = [];
        const weekStart = this.getWeekStart(this.currentDate);

        for (let i = 0; i < 7; i++) {
            const date = moment(weekStart).add(i, 'days');
            days.push({
                date: date.toDate(),
                dayName: date.format('ddd'),
                dayNumber: date.date(),
                isToday: date.isSame(moment(), 'day'),
                events: this.getEventsForDay(date.toDate()),
            });
        }

        return days;
    }

    get currentDayEvents(): any[] {
        return this.getEventsForDay(this.currentDate);
    }

    getWeekStart(date: Date): moment.Moment {
        const weekStart = this.config.weekStartsOn || 1;
        const current = moment(date).day(weekStart);
        if (current.isAfter(date)) {
            current.subtract(7, 'days');
        }
        return current;
    }

    getEventsForDay(day: Date): any[] {
        const dayStart = moment(day).startOf('day');
        const dayEnd = moment(day).endOf('day');

        return this.items.filter((item) => {
            const eventDate = getNestedProperty(item, this.config.dateProperty);
            if (!eventDate) return false;

            const eventMoment = moment(eventDate);
            return eventMoment.isBetween(dayStart, dayEnd, null, '[]');
        });
    }

    getEventTitle(event: any): string {
        return getNestedProperty(event, this.config.titleProperty) || 'Untitled';
    }

    getEventColor(event: any): string {
        if (this.config.colorProperty) {
            const color = getNestedProperty(event, this.config.colorProperty);
            if (color) return color;
        }
        return '#2196f3';
    }

    getEventTime(event: any): string {
        if (!this.config.startTimeProperty) return '';

        const startTime = getNestedProperty(event, this.config.startTimeProperty);
        const endTime = this.config.endTimeProperty ? getNestedProperty(event, this.config.endTimeProperty) : null;

        if (!startTime) return '';

        const start = moment(startTime).format('HH:mm');
        const end = endTime ? moment(endTime).format('HH:mm') : '';

        return end ? `${start} - ${end}` : start;
    }

    getWeekEventStyle(event: any): Record<string, string> {
        const style: Record<string, string> = {};

        if (this.config.startTimeProperty) {
            const startTime = getNestedProperty(event, this.config.startTimeProperty);
            const endTime = this.config.endTimeProperty ? getNestedProperty(event, this.config.endTimeProperty) : null;

            if (startTime) {
                const start = moment(startTime);
                const end = endTime ? moment(endTime) : start.clone().add(1, 'hour');

                const topPercent = (start.hour() + start.minute() / 60) * (100 / 24);
                const durationHours = end.diff(start, 'hours', true);
                const heightPercent = durationHours * (100 / 24);

                style.top = `${topPercent}%`;
                style.height = `${heightPercent}%`;
            }
        }

        style.backgroundColor = this.getEventColor(event);
        return style;
    }

    getDayEventStyle(event: any): Record<string, string> {
        return this.getWeekEventStyle(event);
    }

    formatHour(hour: number): string {
        return `${hour.toString().padStart(2, '0')}:00`;
    }

    previousPeriod() {
        const date = moment(this.currentDate);
        switch (this.viewMode) {
            case 'month':
                this.currentDate = date.subtract(1, 'month').toDate();
                break;
            case 'week':
                this.currentDate = date.subtract(1, 'week').toDate();
                break;
            case 'day':
                this.currentDate = date.subtract(1, 'day').toDate();
                break;
        }
    }

    nextPeriod() {
        const date = moment(this.currentDate);
        switch (this.viewMode) {
            case 'month':
                this.currentDate = date.add(1, 'month').toDate();
                break;
            case 'week':
                this.currentDate = date.add(1, 'week').toDate();
                break;
            case 'day':
                this.currentDate = date.add(1, 'day').toDate();
                break;
        }
    }

    goToToday() {
        this.currentDate = new Date();
    }

    handleDayClick(day: CalendarDay) {
        this.currentDate = day.date;
        this.viewMode = 'day';
    }

    handleEventClick(event: any) {
        this.$emit('item-click', event);
    }

    handleScroll(event: Event) {
        const target = event.target as HTMLElement;
        const scrollHeight = target.scrollHeight;
        const scrollTop = target.scrollTop;
        const clientHeight = target.clientHeight;

        if (scrollHeight - scrollTop - clientHeight < 100) {
            this.$emit('load-more');
        }
    }
}
